import DataTable from "datatables.net-bs5";
import * as Highcharts from "highcharts";
// No extra module import needed — horizontal bar chart is a built-in type in Highcharts core.
import { getChartTheme } from './utils/chartTheme';

export default class {
    static init = () => {
        trakt_watched();
        trakt_genre_chart();   // Phase 176 ENRCH-01: genre breakdown bar chart
        trakt_poster_toggle(); // Phase 176 ENRCH-01: Recent↔Top poster wall toggle
    }
}

function trakt_watched(): void {
    const table = document.querySelector('[data-table="trakt_watched"]') as HTMLTableElement | null;
    if (!table) return;

    new DataTable(table, {
        serverSide: true,
        processing: true,
        ajax: {
            url: window.location.href,
            data: (d: object) => ({ ...d, do: 'getWatched' }),
        },
        columns: [
            // col 0: badge — raw HTML (<span class="badge ...">); do NOT add render:text() or it escapes the span
            { data: 'badge', orderable: true, searchable: true },
            { data: 'title', orderable: true },
            { data: 'detail', orderable: true },
            // col 3: watched — not searchable (datetime string; filter by type/title/detail instead)
            { data: 'watched', orderable: true, searchable: false },
        ],
        order: [[3, 'desc']], // default: watched_at DESC (most recently watched first)
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
    });
}

// ---------------------------------------------------------------------------
// Phase 176 ENRCH-01 — T-1: Genre Breakdown horizontal bar chart
// ---------------------------------------------------------------------------

// trakt_genres is declared in Trakt/default.latte {block page_js}:
//   var trakt_genres = {json_encode($genreBreakdown)|noescape};
// Shape: { "Drama": 45, "Crime": 32, ... }
declare var trakt_genres: Record<string, number> | undefined;

function trakt_genre_chart(): void {
    const el = document.getElementById('trakt-genre-chart');
    if (!el) return;

    if (typeof trakt_genres === 'undefined' || Object.keys(trakt_genres).length === 0) {
        el.innerHTML = '<p class="text-muted text-center py-4">No genre data yet — enrichment cron has not run.</p>';
        return;
    }

    const categories = Object.keys(trakt_genres);
    const values     = Object.values(trakt_genres);

    // Phase 181: getChartTheme() supplies all chrome (background, axis, tooltip surface).
    // D-02: accent series color reads --ksat-accent computed value at construction time.
    // Note: useUTC is set globally in src/index.ts — do NOT set it per-chart.
    // Note: events MUST nest under chart:{} (project memory feedback_highcharts_events_nesting_cast_masks).
    const accentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
    Highcharts.chart(el, Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'bar',
            backgroundColor: 'transparent',
        },
        title: {
            text: '',  // heading is the <h5> above — do NOT set undefined (feedback_highcharts_undefined_options)
        },
        xAxis: {
            categories: categories,
            // lineColor, tickColor, labels.style.color come from getChartTheme()
        },
        yAxis: {
            title: {
                text: 'Titles',
                // color comes from getChartTheme()
            },
            // labels.style.color, gridLineColor come from getChartTheme()
        },
        legend: {
            enabled: false,  // single series — legend adds no value; not legend:undefined
        },
        // tooltip.backgroundColor, tooltip.style.color come from getChartTheme()
        series: [{
            type: 'bar',
            name: 'Titles',
            data: values,
            color: accentHex,  // D-02: reads live --ksat-accent at construction; updateAccentTaggedCharts re-themes on picker change
        }],
        credits: {
            enabled: false,
        },
        plotOptions: {
            bar: {
                borderRadius: 2,
            },
        },
    } as Highcharts.Options));
}

// ---------------------------------------------------------------------------
// Phase 176 ENRCH-01 — T-3: Poster wall Recent↔Top read-only view toggle
// ---------------------------------------------------------------------------
// Read-only view switch: hides/shows two pre-rendered sets by toggling the
// HTML `hidden` attribute. No AJAX, no CSRF, no state mutation (T-176-11 accept).

function trakt_poster_toggle(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('.btn-group [data-target]');
    if (!buttons.length) return;

    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            const target = btn.dataset.target;
            if (!target) return;

            // Show the matching poster set, hide the other
            document.querySelectorAll<HTMLElement>('.trakt-poster-set').forEach(el => {
                el.hidden = el.id !== `trakt-posters-${target}`;
            });

            // Swap active/inactive button styling + aria-pressed
            buttons.forEach(b => {
                const isActive = b.dataset.target === target;
                b.classList.toggle('btn-primary', isActive);
                b.classList.toggle('btn-outline-secondary', !isActive);
                b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        });
    });
}
